import { API, Auth } from 'aws-amplify';
import { videosByOwnerAndDate } from '../graphql/queries';
import { getSignedUrl } from './storage';

export interface CaptionCornerModel {
  id: string;
  name: string;
  filename: string;
  statusMessage: string;
  srt?: string;
  vtt?: string;
  json?: string; // Keep for backward compatibility
  docx?: string; // New Word document field
  languageToDisplay: string;
  translationLanguage?: string;
  translationLanguageToDisplay?: string;
  translationStatus?: string;
  translatedSrt?: string;
  translatedVtt?: string;
  translatedDocx?: string;
  description?: string;
  costSavings?: number;
  createDate: string;
}

export const fetchCaptionCornerModels = async (): Promise<CaptionCornerModel[]> => {
  try {
    // Get current user to construct owner ID
    const currentUser = await Auth.currentAuthenticatedUser();
    const userSub = currentUser.attributes && currentUser.attributes.sub ? currentUser.attributes.sub : '';
    const ownerID = `${userSub}::${currentUser.username}`;
    
    console.log('Using efficient videosByOwnerAndDate query for owner:', ownerID);

    const response = await API.graphql({
      query: videosByOwnerAndDate,
      variables: {
        owner: ownerID,
        sortDirection: 'DESC', // Newest first
        limit: 100 // Increase limit to get more videos
      }
    });

    // display the response it is getting as it is to the console
    console.log("Response from the backend as it is:",response);
    console.log("Number of items returned:", response.data.videosByOwnerAndDate.items.length);
    
    const files = response.data.videosByOwnerAndDate.items;
    
    // Debug: Log the owner field of each item
    files.forEach((file: any, index: number) => {
      console.log(`File ${index + 1} details:`, {
        id: file.id,
        name: file.name,
        filename: file.filename,
        owner: file.owner,
        createdAt: file.createdAt,
        createDate: file.createDate
      });
    });
    
    // Get signed URLs for SRT, VTT, DOCX files and their translated versions
    const filesWithUrls = await Promise.all(
      files.map(async (file: CaptionCornerModel) => {
        // Handle DOCX files (new) and JSON files (backward compatibility)
        let docxUrl;
        let jsonUrl;
        
        if (file.docx) {
          // New records with DOCX field
          docxUrl = await getSignedUrl(file.docx);
        } else if (file.srt) {
          // For older records, try to derive DOCX path from SRT path
          const docxPath = file.srt.replace(/\.srt$/, '.docx');
          try {
            docxUrl = await getSignedUrl(docxPath);
          } catch (error) {
            console.error(`Error getting signed URL for derived DOCX path ${docxPath}:`, error);
            docxUrl = undefined;
          }
        }
        
        // Keep JSON for backward compatibility (internal use)
        if (file.json) {
          jsonUrl = await getSignedUrl(file.json);
        } else if (file.srt) {
          // Derive JSON path from SRT path for backward compatibility
          const jsonPath = file.srt.replace(/\.srt$/, '.json');
          try {
            jsonUrl = await getSignedUrl(jsonPath);
          } catch (error) {
            console.error(`Error getting signed URL for derived JSON path ${jsonPath}:`, error);
            jsonUrl = undefined;
          }
        }

        // Handle translated files
        let translatedSrtUrl;
        let translatedVttUrl;
        let translatedDocxUrl;

        if (file.translatedSrt) {
          try {
            translatedSrtUrl = await getSignedUrl(file.translatedSrt);
          } catch (error) {
            console.error(`Error getting signed URL for translated SRT ${file.translatedSrt}:`, error);
            translatedSrtUrl = undefined;
          }
        }

        if (file.translatedVtt) {
          try {
            translatedVttUrl = await getSignedUrl(file.translatedVtt);
          } catch (error) {
            console.error(`Error getting signed URL for translated VTT ${file.translatedVtt}:`, error);
            translatedVttUrl = undefined;
          }
        }

        if (file.translatedDocx) {
          try {
            translatedDocxUrl = await getSignedUrl(file.translatedDocx);
          } catch (error) {
            console.error(`Error getting signed URL for translated DOCX ${file.translatedDocx}:`, error);
            translatedDocxUrl = undefined;
          }
        }

        return {
          ...file,
          srt: file.srt ? await getSignedUrl(file.srt) : undefined,
          vtt: file.vtt ? await getSignedUrl(file.vtt) : undefined,
          json: jsonUrl, // Keep for backward compatibility
          docx: docxUrl, // New Word document field
          translatedSrt: translatedSrtUrl,
          translatedVtt: translatedVttUrl,
          translatedDocx: translatedDocxUrl,
        };
      })
    );

    return filesWithUrls;
  } catch (error) {
    console.error('Error fetching caption corner models:', error);
    return [];
  }
};